import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuItem from '@mui/material/MenuItem';
import LinkIcon from '@mui/icons-material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import { Translate } from '../../../../../../../shared/LanguageService';
import FailureSnackbar from '../../../../../../../shared/feedback/FailureSnackbar';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { Resolution } from '../../../Entities';
import CopyLinkModal from '../../../CopyLinkModal';
import { useDeliveryOptions } from '../state/use-delivery-options';
import { canUserDownload } from '../state/utils';

type DeliveryOptionsDropdownProps = {
    user: AuthenticatedUser;
    photoStatus: number;
    normalImagesAvailable: boolean;
    numberOfDeliverableImages: number;
    orderId: number;
};

type StyledMenuItemProps = {
    onClick: () => void;
    text: string;
    icon: React.ReactNode;
};

function StyledMenuItem({ onClick, text, icon }: StyledMenuItemProps) {
    return (
        <MenuItem onClick={onClick}>
            <ListItemText
                primaryTypographyProps={{
                    fontSize: '1.5rem'
                }}
            >
                {text}
            </ListItemText>
            <ListItemIcon sx={{ fontSize: 'large', paddingLeft: '1rem' }}>
                {icon}
            </ListItemIcon>
        </MenuItem>
    );
}

const DeliveryButton = styled(Button)({
    borderRadius: 0,
    height: '38px',
    maxWidth: '100%',
    minWidth: '320px',
    fontSize: '1.4rem',
    justifyContent: 'center',
    whiteSpace: 'nowrap'
});

export function DeliveryOptionsDropdown({
    user,
    photoStatus,
    normalImagesAvailable,
    numberOfDeliverableImages,
    orderId
}: DeliveryOptionsDropdownProps) {
    let [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    let open = Boolean(anchorEl);

    let {
        downloadingResolution,
        downloadLink,
        isLinkModalOpen,
        showError,
        getDownloadLink,
        downloadHighResolutionImages,
        downloadNormalResolutionImages,
        closeLinkModal,
        closeError
    } = useDeliveryOptions({ orderId, user });

    let showDownloadOptions = canUserDownload(user, photoStatus, numberOfDeliverableImages);

    function handleOpenMenu(event: React.MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
    }

    function handleCloseMenu() {
        setAnchorEl(null);
    }

    function handleDownloadLink(resolution: Resolution) {
        getDownloadLink(resolution);
        handleCloseMenu();
    }

    function handleDownloadHighResolution() {
        handleCloseMenu();
        downloadHighResolutionImages();
    }

    function handleDownloadNormalResolution() {
        handleCloseMenu();
        downloadNormalResolutionImages();
    }

    let isLoading = downloadingResolution !== null;

    return (
        <nav aria-label={Translate('image-processing/edited/button-label/download-or-send-images')}>
            <CopyLinkModal
                open={isLinkModalOpen}
                onClose={closeLinkModal}
                link={downloadLink}
            />

            <FailureSnackbar
                open={showError}
                onClose={closeError}
                text={Translate('image-processing/edited/delivery-options/error')}
            />

            <DeliveryButton
                type="button"
                variant="contained"
                id="delivery-options-button"
                aria-controls={open ? 'delivery-options-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleOpenMenu}
                disabled={isLoading}
                aria-busy={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                endIcon={
                    isLoading ? undefined : open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
                }
            >
                {Translate('image-processing/edited/button-label/download-or-send-images')}
            </DeliveryButton>

            <Menu
                id="delivery-options-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                MenuListProps={{ 'aria-labelledby': 'delivery-options-button' }}
                sx={{ borderRadius: 0 }}
            >
                {showDownloadOptions ? (
                    <Box>
                        <StyledMenuItem
                            onClick={handleDownloadHighResolution}
                            text={Translate('image-processing/edited/button-label/download-high-images')}
                            icon={<CloudDownloadIcon fontSize="large" />}
                        />
                        {normalImagesAvailable ? <Divider /> : null}
                        {normalImagesAvailable ? (
                            <StyledMenuItem
                                onClick={handleDownloadNormalResolution}
                                text={Translate('image-processing/edited/button-label/download-normal-images')}
                                icon={<CloudDownloadIcon fontSize="large" />}
                            />
                        ) : null}
                        <Divider />
                    </Box>
                ) : null}

                {showDownloadOptions ? (
                    <Box>
                        <StyledMenuItem
                            onClick={() => handleDownloadLink(Resolution.High)}
                            text={Translate('image-processing/deliver/zip-download-link-high')}
                            icon={<LinkIcon fontSize="large" />}
                        />
                        {normalImagesAvailable ? <Divider /> : null}
                        {normalImagesAvailable ? (
                            <StyledMenuItem
                                onClick={() => handleDownloadLink(Resolution.Normal)}
                                text={Translate('image-processing/deliver/zip-download-link-normal')}
                                icon={<LinkIcon fontSize="large" />}
                            />
                        ) : null}
                    </Box>
                ) : null}
            </Menu>
        </nav>
    );
}
